import { useMemo, type ReactNode } from 'react';
import { Page, PageBody } from '@customerio/pluma-components/react';
import { useParams, useRouter } from '@tanstack/react-router';
import { DocumentTitle } from '@/components/page/document-title';
import { CollectionDetailHeader, useCollectionWorkspaceLimits } from '@/features/collections';
import { useRegisterBreadcrumbs, type BreadcrumbItem } from '@/services/breadcrumbs';
import { useCollection } from '@/services/db/collection/queries';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';

type CollectionDetailPageShellProps = {
  children?: ReactNode;
  titleSuffix?: string;
};

export function CollectionDetailPageShell({ children, titleSuffix }: CollectionDetailPageShellProps) {
  const { collectionId } = useParams({ strict: false });
  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;
  const router = useRouter();
  const { data: collection } = useCollection({ id: collectionId ?? '' });
  const limits = useCollectionWorkspaceLimits(workspaceId);
  const title = titleSuffix == null ? collection.name : `${collection.name} ${titleSuffix}`;
  const breadcrumbs = useMemo<BreadcrumbItem[]>(
    () => [
      {
        id: 'collections',
        text: 'Collections',
        href: router.buildLocation({ to: '/workspaces/$workspaceId/collections', params: { workspaceId } }).href,
      },
      {
        id: `collection-${collection.id}`,
        text: collection.name,
        href: router.buildLocation({
          to: '/workspaces/$workspaceId/collections/$collectionId',
          params: { workspaceId, collectionId: collection.id },
        }).href,
        isTruncated: true,
      },
    ],
    [collection.id, collection.name, router, workspaceId],
  );
  useRegisterBreadcrumbs(breadcrumbs);

  const handleCollectionDeleted = () => {
    void router.navigate({ to: '/workspaces/$workspaceId/collections', params: { workspaceId } });
  };

  return (
    <>
      <DocumentTitle title={title} />
      <Page flexGrow="1">
        <CollectionDetailHeader
          collection={collection}
          maxFileSizeBytes={limits.maxFileSizeBytes}
          workspaceId={workspaceId}
          onDeleted={handleCollectionDeleted}
        />
        <PageBody>{children}</PageBody>
      </Page>
    </>
  );
}
